import { memo } from 'react';
import type { PresenceDotProps } from '@cyphex/ui';
import { usePersonSnapshot } from '../hooks/usePresence';
import { PresenceIndicator } from './PresenceIndicator';

export interface PersonPresenceDotProps {
  userId: string;
  size?: PresenceDotProps['size'];
  showLabel?: boolean;
  ringClassName?: string;
  className?: string;
}

/**
 * Live presence for one person, self-subscribing.
 *
 * Mount it only where the person is actually visible — the subscription is
 * released on unmount, so a virtualised roster watches the viewport, not the
 * whole directory.
 */
export const PersonPresenceDot = memo(function PersonPresenceDot({
  userId, size = 'sm', showLabel = false, ringClassName, className,
}: PersonPresenceDotProps) {
  /* The whole snapshot, not just the state: this dot is often the only presence
     affordance on its row, and "when were they last here" is precisely what a
     colour cannot say. It goes into the dot's accessible name and tooltip. */
  const snapshot = usePersonSnapshot(userId);

  return (
    <PresenceIndicator
      state={snapshot?.effectiveState ?? 'offline'}
      lastActivityAt={snapshot?.resolved === true ? snapshot.lastActivityAt : null}
      size={size}
      showLabel={showLabel}
      ringClassName={ringClassName}
      className={className}
    />
  );
});
